import { useEffect, useState } from "react";
import { MapPin, ArrowUpRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { Reveal, Overline } from "@/components/Reveal";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const localeMap = { en: "en-CA", fr: "fr-CA", es: "es-ES", pt: "pt-BR", it: "it-IT" };

export const Events = () => {
  const { t, lang } = useLang();
  const [events, setEvents] = useState([]);
  const [active, setActive] = useState(null); // event being RSVP'd
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data)).catch(() => setEvents([]));
  }, []);

  const fmt = (iso) => {
    const d = new Date(iso);
    return {
      date: d.toLocaleDateString(localeMap[lang] || "en-CA", { month: "short", day: "2-digit" }),
      time: d.toLocaleTimeString(localeMap[lang] || "en-CA", { hour: "2-digit", minute: "2-digit" }),
      year: d.getFullYear(),
    };
  };

  const openRsvp = (e) => { setActive(e); setName(""); setEmail(""); };

  const submitRsvp = async (ev) => {
    ev.preventDefault();
    setLoading(true);
    try {
      await api.post(`/events/${active.id}/rsvp`, { name, email });
      toast.success(t.events.rsvpSuccess);
      setActive(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-transparent border border-white/20 rounded-sm px-4 py-3 text-white placeholder-white/35 focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/30 transition-colors";

  return (
    <section id="events" data-testid="events-section" className="py-24 md:py-40 max-w-7xl mx-auto px-6 md:px-12">
      <div className="mb-14">
        <Reveal><Overline>{t.events.overline}</Overline></Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-display uppercase text-5xl sm:text-7xl leading-[0.9] mt-4">{t.events.title}</h2>
        </Reveal>
      </div>

      {events.length === 0 ? (
        <p className="text-white/50 text-lg" data-testid="events-empty">{t.events.empty}</p>
      ) : (
        <div className="border-t border-white/10">
          {events.map((e, i) => {
            const f = fmt(e.starts_at);
            return (
              <Reveal key={e.id} delay={i * 0.05}>
                <div
                  data-testid={`event-row-${i}`}
                  className="group grid md:grid-cols-12 gap-4 md:gap-8 items-center py-8 border-b border-white/10 transition-colors hover:bg-white/[0.02]"
                >
                  <div className="md:col-span-2 flex md:block items-baseline gap-3">
                    <div className="font-display text-4xl md:text-5xl text-purple-400 uppercase">{f.date}</div>
                    <div className="text-white/40 text-sm">{f.year} · {f.time}</div>
                  </div>
                  <div className="md:col-span-6">
                    <h3 className="font-display uppercase text-2xl md:text-3xl tracking-tight">{e.title}</h3>
                    <p className="text-white/55 mt-2 font-light max-w-lg">{e.description}</p>
                  </div>
                  <div className="md:col-span-3 flex items-center gap-2 text-white/60">
                    <MapPin size={16} className="text-purple-400 shrink-0" />
                    <span className="text-sm">{e.location}</span>
                  </div>
                  <div className="md:col-span-1 flex md:justify-end">
                    <button
                      onClick={() => openRsvp(e)}
                      data-testid={`event-rsvp-${i}`}
                      className="inline-flex items-center gap-1 text-sm uppercase tracking-wide text-white/70 group-hover:text-purple-300 transition-colors"
                    >
                      {t.events.rsvp} <ArrowUpRight size={16} />
                    </button>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      )}

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="bg-[#0c0c0c] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-display uppercase text-2xl">{t.events.rsvpTitle}</DialogTitle>
          </DialogHeader>
          {active && (
            <form onSubmit={submitRsvp} data-testid="rsvp-form" className="space-y-4 pt-2">
              <div className="text-sm text-purple-300">{active.title}</div>
              <input required data-testid="rsvp-name" className={inputCls} placeholder={t.events.rsvpName} value={name} onChange={(e) => setName(e.target.value)} />
              <input required type="email" data-testid="rsvp-email" className={inputCls} placeholder={t.events.rsvpEmail} value={email} onChange={(e) => setEmail(e.target.value)} />
              <button type="submit" disabled={loading} data-testid="rsvp-submit" className="w-full py-3.5 rounded-full bg-white text-black font-semibold hover:bg-purple-500 hover:text-white transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                {loading ? <Loader2 size={18} className="animate-spin" /> : t.events.rsvpSubmit}
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
